import React, { createContext, useContext, useState } from 'react';

const TourContext = createContext();

// One global key — dismissing on ANY page prevents ALL tours from auto-starting.
const GLOBAL_DISMISSED_KEY = 'haix_tour_globally_dismissed';
const TOUR_STORAGE_KEY = 'haix_tour_completed';
const BRAND_INTEL_TOUR_KEY = 'haix_brandintel_tour_completed';
const TRACK_COMPANIES_TOUR_KEY = 'haix_trackcompanies_tour_completed';
const LINKEDIN_PUBLIC_TOUR_KEY = 'haix_linkedin_public_tour_completed';
const LINKEDIN_OWNED_TOUR_KEY = 'haix_linkedin_owned_tour_completed';
const ADD_COMPANY_TOUR_KEY = 'haix_addcompany_tour_completed';

const isTourDismissed = () =>
  localStorage.getItem(GLOBAL_DISMISSED_KEY) === 'true';

const dismissTourGlobally = () =>
  localStorage.setItem(GLOBAL_DISMISSED_KEY, 'true');

export const TourProvider = ({ children }) => {
  const hasCompleted =
    localStorage.getItem(TOUR_STORAGE_KEY) === 'true' ||
    localStorage.getItem(BRAND_INTEL_TOUR_KEY) === 'true' ||
    localStorage.getItem(LINKEDIN_PUBLIC_TOUR_KEY) === 'true' ||
    localStorage.getItem(LINKEDIN_OWNED_TOUR_KEY) === 'true' ||
    localStorage.getItem(TRACK_COMPANIES_TOUR_KEY) === 'true';
  const [tourActive, setTourActive] = useState(!hasCompleted && !isTourDismissed());
  const [tourKey, setTourKey] = useState(0);

  const startTour = () => {
    if (isTourDismissed()) return;
    localStorage.setItem(TOUR_STORAGE_KEY, 'true');
    setTourKey((prev) => prev + 1);
    setTourActive(true);
  };

  // ── Brand & Market Intel Dashboard tour ──
  const brandIntelHasCompleted = localStorage.getItem(BRAND_INTEL_TOUR_KEY) === 'true';
  const [brandIntelTourActive, setBrandIntelTourActive] = useState(!brandIntelHasCompleted && !isTourDismissed());
  const [brandIntelTourKey, setBrandIntelTourKey] = useState(0);

  const startBrandIntelTour = () => {
    if (isTourDismissed()) return;
    localStorage.setItem(BRAND_INTEL_TOUR_KEY, 'true');
    setBrandIntelTourKey((prev) => prev + 1);
    setBrandIntelTourActive(true);
  };

  // ── Track Companies / Manage Brands tour ──
  const trackCompaniesHasCompleted = localStorage.getItem(TRACK_COMPANIES_TOUR_KEY) === 'true';
  const [trackCompaniesTourActive, setTrackCompaniesTourActive] = useState(!trackCompaniesHasCompleted && !isTourDismissed());
  const [trackCompaniesTourKey, setTrackCompaniesTourKey] = useState(0);

  const startTrackCompaniesTour = () => {
    if (isTourDismissed()) return;
    localStorage.setItem(TRACK_COMPANIES_TOUR_KEY, 'true');
    setTrackCompaniesTourKey((prev) => prev + 1);
    setTrackCompaniesTourActive(true);
  };

  // ── LinkedIn Public Page Insights tour ──
  const linkedinPublicHasCompleted = localStorage.getItem(LINKEDIN_PUBLIC_TOUR_KEY) === 'true';
  const [linkedinPublicTourActive, setLinkedinPublicTourActive] = useState(!linkedinPublicHasCompleted && !isTourDismissed());
  const [linkedinPublicTourKey, setLinkedinPublicTourKey] = useState(0);

  const startLinkedinPublicTour = () => {
    if (isTourDismissed()) return;
    localStorage.setItem(LINKEDIN_PUBLIC_TOUR_KEY, 'true');
    setLinkedinPublicTourKey((prev) => prev + 1);
    setLinkedinPublicTourActive(true);
  };

  // ── LinkedIn Owned Account Insights tour ──
  const linkedinOwnedHasCompleted = localStorage.getItem(LINKEDIN_OWNED_TOUR_KEY) === 'true';
  const [linkedinOwnedTourActive, setLinkedinOwnedTourActive] = useState(!linkedinOwnedHasCompleted && !isTourDismissed());
  const [linkedinOwnedTourKey, setLinkedinOwnedTourKey] = useState(0);

  const startLinkedinOwnedTour = () => {
    if (isTourDismissed()) return;
    localStorage.setItem(LINKEDIN_OWNED_TOUR_KEY, 'true');
    setLinkedinOwnedTourKey((prev) => prev + 1);
    setLinkedinOwnedTourActive(true);
  };

  // ── Add / Edit Company form tour ──
  const [addCompanyTourActive, setAddCompanyTourActive] = useState(false);
  const [addCompanyTourKey, setAddCompanyTourKey] = useState(0);

  const startAddCompanyTour = () => {
    if (isTourDismissed()) return;
    localStorage.setItem(ADD_COMPANY_TOUR_KEY, 'true');
    setAddCompanyTourKey((prev) => prev + 1);
    setAddCompanyTourActive(true);
  };

  // Each tour's own "active" state is computed independently on mount (its
  // own localStorage key, checked once), so on a page where more than one
  // happens to be unfinished at the same time — e.g. arriving here from
  // another page without a full reload — they all start active and stack as
  // separate overlays. Previously, closing one only marked THAT tour done,
  // so closing it just uncovered the next one underneath, each with its own
  // beacon/loading-spinner animation — reported as "closing the tour shows
  // a loader and blocks the screen". Closing any one tour now ends all of
  // them, so dismissing the tour actually dismisses it.
  const endAllTours = () => {
    dismissTourGlobally();
    localStorage.setItem(TOUR_STORAGE_KEY, 'true');
    localStorage.setItem(BRAND_INTEL_TOUR_KEY, 'true');
    localStorage.setItem(TRACK_COMPANIES_TOUR_KEY, 'true');
    localStorage.setItem(LINKEDIN_PUBLIC_TOUR_KEY, 'true');
    localStorage.setItem(LINKEDIN_OWNED_TOUR_KEY, 'true');
    localStorage.setItem(ADD_COMPANY_TOUR_KEY, 'true');
    setTourActive(false);
    setBrandIntelTourActive(false);
    setTrackCompaniesTourActive(false);
    setLinkedinPublicTourActive(false);
    setLinkedinOwnedTourActive(false);
    setAddCompanyTourActive(false);
  };

  const endTour = endAllTours;
  const endBrandIntelTour = endAllTours;
  const endTrackCompaniesTour = endAllTours;
  const endLinkedinPublicTour = endAllTours;
  const endLinkedinOwnedTour = endAllTours;
  const endAddCompanyTour = endAllTours;

  return (
    <TourContext.Provider
      value={{
        tourActive,
        tourKey,
        startTour,
        endTour,
        brandIntelTourActive,
        brandIntelTourKey,
        startBrandIntelTour,
        endBrandIntelTour,
        trackCompaniesTourActive,
        trackCompaniesTourKey,
        startTrackCompaniesTour,
        endTrackCompaniesTour,
        linkedinPublicTourActive,
        linkedinPublicTourKey,
        startLinkedinPublicTour,
        endLinkedinPublicTour,
        linkedinOwnedTourActive,
        linkedinOwnedTourKey,
        startLinkedinOwnedTour,
        endLinkedinOwnedTour,
        addCompanyTourActive,
        addCompanyTourKey,
        startAddCompanyTour,
        endAddCompanyTour,
      }}
    >
      {/* Force Joyride button colours — overrides any global Tailwind button resets */}
      <style>{`
        button[data-action="primary"] {
          background-color: #3b82f6 !important;
          color: #ffffff !important;
          border: none !important;
          cursor: pointer !important;
        }
        button[data-action="primary"]:hover {
          background-color: #2563eb !important;
        }
        button[data-action="back"] {
          background-color: transparent !important;
          color: #64748b !important;
          border: none !important;
          cursor: pointer !important;
        }
        button[data-action="skip"] {
          background-color: transparent !important;
          color: #94a3b8 !important;
          border: none !important;
          cursor: pointer !important;
        }
        button[data-action="close"] {
          background-color: transparent !important;
          border: none !important;
          cursor: pointer !important;
        }
      `}</style>
      {children}
    </TourContext.Provider>
  );
};

export const useTour = () => useContext(TourContext);
